import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { maxBy } from 'lodash';
import { PER_PAGE_100 } from '../../../constants/common';
import {
  getShopCategoriesListRepository,
  postCreateShopCategoriesRepository,
  updateShopCategoriesRepository,
} from '../../../repository/mytm/shop-categories/shopCategoriesRepository';
import { ShopCategoriesPageHeader } from '../../organisms/shop-categories/ShopCategoriesPageHeader';
import { ShopCategoriesTable } from '../../organisms/shop-categories/ShopCategoriesTable';
import { appStyles } from '../../../styles';
import { ShopCategoriesType } from '../../../types/shopCategories';
import { SHOP_CATEGORIES } from '../../../constants/define/translation';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { MESSAGE_ERROR } from '../../../constants/message';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

export const Categories = () => {
  const [shopCategories, setShopCategories] = useState<ShopCategoriesType[]>([]);
  const [pageActive, setPageActive] = useState(1);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(SHOP_CATEGORIES);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);
  const errorCount = useRef(0);

  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [countrySelected] : null,
    () =>
      getShopCategoriesListRepository({
        page: 1,
        take: PER_PAGE_100,
        country: countrySelected!,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  useEffect(() => {
    setShopCategories(handleData(data?.shopCategories || []));
  }, [data]);

  const handleData = (dataApi: Array<any>) => {
    let addFakeData = 0;
    const currentCount = dataApi.length || 0;
    const newData = [...dataApi] || [];
    const desiredMultiple = 10;
    const n = currentCount / desiredMultiple;
    const roundedCount = Math.ceil(n) * desiredMultiple;
    if (!currentCount) {
      addFakeData = 10;
    } else if (n % 1 === 0) {
      addFakeData = roundedCount + desiredMultiple - currentCount;
    } else {
      addFakeData = roundedCount - currentCount;
    }
    for (let i = 0; i < addFakeData; i += 1) {
      newData.push({ disabled: true });
    }
    return newData.map((item: any, index: number) => ({ ...item, index, isInEditMode: false }));
  };

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
    window.scrollTo(0, 0);
  }, []);

  const resetInitialData = () => {
    setShopCategories(handleData(data?.shopCategories || []));
  };

  const onFocusNewRow = () => {
    resetInitialData();
  };

  const handleChangeEditMode = (index: number) => {
    if (index >= 0) {
      const newData = [...handleData(data?.shopCategories || [])];
      const item = newData.find(val => val?.index === index);
      if (item) {
        newData[index].isInEditMode = true;
      }
      setShopCategories(newData);
    }
  };

  const handleChangeInputLink = (index: number, value: string) => {
    const newData = [...shopCategories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      newData[index].ctaLink = value;
    }
    setShopCategories(newData);
  };

  const handleChangeInputTitle = (index: number, value: string) => {
    const newData = [...shopCategories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      newData[index].title = value;
    }
    setShopCategories(newData);
  };

  const removeItemByIndex = (index: number) => {
    const newData = [...shopCategories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      newData.splice(index, 1);
    }

    setShopCategories(newData);
  };

  const handleAddLink = (index: number) => {
    const newData = [...shopCategories];
    const item = newData.find(val => val?.index === index);
    if (item) {
      const url = item?.ctaLink;
      const checkExistTitle = data?.shopCategories?.find(it => {
        if (item.id) {
          return it.title?.toLowerCase() === item.title?.toLowerCase() && it.id !== item?.id;
        }
        return it.title?.toLowerCase() === item.title?.toLowerCase();
      });
      if (checkExistTitle) {
        message.error(t('message.error.uniqueTitle'));
        return;
      }
      if (!item?.title) {
        // message.error('Title is required');
        return;
      }
      if (!url) {
        // message.error('URL is required');
        return;
      }
      const isValidUrl = isValidLinkUrl(url);
      if (!isValidUrl) {
        message.error(t('message.error.insertValidUrl'));
        return;
      }
      try {
        const lastCategory = maxBy(data?.shopCategories, 'sortOrder');
        setLoading(true);
        const payloadToAdd = {
          title: item?.title,
          ctaLink: item?.ctaLink,
          country: countrySelected,
          disabled: false,
          sortOrder: (lastCategory?.sortOrder || 0) + 1,
        };
        const payloadToUpdate = {
          title: item?.title,
          ctaLink: item?.ctaLink,
          id: item?.id,
        };
        if (item?.id) {
          updateShopCategoriesRepository(payloadToUpdate)
            .then(() => {
              mutate();
              setLoading(false);
              message.success(t('message.success.updateShopCategory'));
            })
            .catch(e => {
              mutate();
              setLoading(false);
              message.error(e?.errorMessage);
            });
        } else {
          postCreateShopCategoriesRepository(payloadToAdd)
            .then(() => {
              mutate();
              setLoading(false);
              message.success(t('message.success.createShopCategory'));
            })
            .catch(e => {
              mutate();
              setLoading(false);
              message.error(e?.errorMessage);
            });
        }

        return;
      } catch (err) {
        setLoading(false);
        if (item?.id) {
          message.error(t('message.error.updateShopCategory'));
        } else {
          message.error(t('message.error.createShopCategory'));
        }
      }
    }
  };

  const isValidLinkUrl = (url: string) => {
    if (!url) return false;
    const regex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?$/i;
    return regex.test(url);
  };

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  return (
    <Container>
      <ShopCategoriesPageHeader />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      {(!shopCategories || !shopCategories.length) && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {shopCategories && shopCategories.length && (
        <ShopCategoriesTable
          handleChangePageActive={handleChangePageActive}
          handleChangeInputLink={handleChangeInputLink}
          handleChangeInputTitle={handleChangeInputTitle}
          handleChangeEditMode={handleChangeEditMode}
          handleAddLink={handleAddLink}
          onFocusNewRow={onFocusNewRow}
          removeItemByIndex={removeItemByIndex}
          onRefresh={mutate}
          records={shopCategories}
          loading={loading || isValidating}
          setLoading={setLoading}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_100,
            current: pageActive,
            total: shopCategories?.length,
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
